/**
 * Storefront-account hydration.
 *
 * Fetches the customer-account payload from the Laravel storefront API via
 * the Shopify App Proxy (/apps/mills/me) and replaces every [data-hydrate]
 * placeholder in the page with the system's authoritative value.
 *
 * The Liquid templates still render server-side values as fallback content,
 * so if this script fails to fetch (App Proxy not configured, network error,
 * 401) the user keeps seeing the pre-existing metafield-driven values — no
 * worse than today's behavior. On success the placeholders show the truth
 * from the Laravel DB.
 */
(function (w, d) {
  "use strict";

  if (w.MillsHydrate) return;

  var CONFIG = {
    fallbackBase: "https://mills.lets.co.il/storefront",
    cacheKey: "mills.me.v1",
    cacheTtlMs: 60 * 1000,
    devCustomerHeader: "X-Mills-Dev-Customer-Id",
    metaBase: "mills-storefront-api-base",
    metaToken: "mills-storefront-token",
  };

  function readMeta(name) {
    var el = d.querySelector('meta[name="' + name + '"]');
    return el ? (el.getAttribute("content") || "").trim() : "";
  }

  // Admin "Preview as customer" passes a short-lived read-only token via
  // ?mills_preview= on the preview page; it takes precedence over the meta tag.
  function readPreviewToken() {
    try {
      var m = (w.location.search || "").match(/[?&]mills_preview=([^&]+)/);
      return m ? decodeURIComponent(m[1]) : "";
    } catch (_) {
      return "";
    }
  }

  // Expose a global preview flag so other modules (e.g. the card-update modal)
  // can keep read-only behaviour on the admin "Preview as customer" page.
  w.MillsDogDashboard = w.MillsDogDashboard || {};
  w.MillsDogDashboard.IS_PREVIEW = readPreviewToken() !== "";

  function buildEndpoint() {
    var base = w.MillsAuth ? w.MillsAuth.base() : (readMeta(CONFIG.metaBase) || CONFIG.fallbackBase);
    return base.replace(/\/$/, "") + "/me";
  }

  var STATUS_TEXT = {
    active: "פעיל",
    paused: "מושהה",
    pending: "מושהה",
    disable: "לא פעיל",
    disabled: "לא פעיל",
    cancelled: "בוטל",
    canceled: "בוטל",
  };

  var STATUS_PILL_CLASS = {
    active: "status-active",
    paused: "status-paused",
    pending: "status-pending",
    disable: "status-cancelled",
    disabled: "status-cancelled",
    cancelled: "status-cancelled",
    canceled: "status-cancelled",
  };

  // v1 said "2 Months", v2 says "Every 2 Months". A key that misses here falls through
  // to the raw English, which is how customers were shown "Every 2 Months".
  var FREQUENCY_TEXT = {
    Monthly: "כל חודש",
    "Every Month": "כל חודש",
    "2 Months": "כל חודשיים",
    "Every 2 Months": "כל חודשיים",
    "Two months": "כל חודשיים",
  };

  function frequencyText(raw) {
    var s = String(raw || "").trim();
    if (!s) return "";
    if (FREQUENCY_TEXT[s]) return FREQUENCY_TEXT[s];

    // Unknown wording: decide on the only thing that matters rather than
    // showing a customer an English string we failed to look up.
    return /2|two|חודשיים/i.test(s) ? "כל חודשיים" : "כל חודש";
  }

  function shouldRun() {
    return Boolean(
      d.getElementById("dog-dashboard-section") ||
        d.getElementById("mini-cart") ||
        d.querySelector("[data-hydrate]")
    );
  }

  function readCache() {
    try {
      var raw = w.sessionStorage.getItem(CONFIG.cacheKey);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.savedAt !== "number") return null;
      if (Date.now() - parsed.savedAt > CONFIG.cacheTtlMs) return null;
      return parsed.payload || null;
    } catch (_) {
      return null;
    }
  }

  function writeCache(payload) {
    try {
      w.sessionStorage.setItem(
        CONFIG.cacheKey,
        JSON.stringify({ savedAt: Date.now(), payload: payload })
      );
    } catch (_) {
      /* sessionStorage may be full or disabled — non-fatal */
    }
  }

  function invalidateCache() {
    try {
      w.sessionStorage.removeItem(CONFIG.cacheKey);
    } catch (_) {}
  }

  function fetchMe() {
    var headers = { Accept: "application/json" };
    // Shared resolver (localStorage -> URL -> meta) so an SMS-logged-in customer is recognised.
    var token = w.MillsAuth ? w.MillsAuth.token() : (readPreviewToken() || readMeta(CONFIG.metaToken));

    if (token) {
      headers["Authorization"] = "Bearer " + token;
    } else {
      var devCustomerId =
        (w.MillsDashboardConfig && w.MillsDashboardConfig.devCustomerId) || "";
      if (devCustomerId) headers[CONFIG.devCustomerHeader] = devCustomerId;
    }

    return fetch(buildEndpoint(), {
      method: "GET",
      headers: headers,
      credentials: "omit",
      mode: "cors",
    }).then(function (res) {
      /*
       * The one request every dashboard visit makes — so this is what actually keeps an
       * SMS login alive: the server hands back a fresher token as the stored one ages,
       * and a customer who merely LOOKS at their account never has to log in again.
       */
      if (w.MillsAuth && w.MillsAuth.absorbRenewal) w.MillsAuth.absorbRenewal(res);

      if (!res.ok) {
        var err = new Error("storefront_api_error");
        err.status = res.status;
        throw err;
      }
      return res.json();
    });
  }

  function pickActiveSubscription(subscriptions) {
    if (!Array.isArray(subscriptions) || subscriptions.length === 0) return null;
    for (var i = 0; i < subscriptions.length; i++) {
      var s = subscriptions[i];
      var status = (s && s.status ? String(s.status) : "").toLowerCase().trim();
      if (status === "active") return s;
    }
    return subscriptions[0];
  }

  function formatChargeCycle(raw) {
    if (!raw) return "";
    var iso = String(raw).split("T")[0];
    var parts = iso.split("-");
    if (parts.length !== 3) return String(raw);
    return parts[2] + "." + parts[1] + "." + parts[0];
  }

  function setText(el, text) {
    if (el && typeof text === "string") el.textContent = text;
  }

  function setAttr(el, name, value) {
    if (!el) return;
    if (value === null || value === undefined || value === "") {
      el.removeAttribute(name);
    } else {
      el.setAttribute(name, String(value));
    }
  }

  function replacePillClass(el, statusKey) {
    if (!el) return;
    var className = el.className || "";
    className = className.replace(/\bstatus-(active|paused|pending|cancelled)\b/g, "").trim();
    var add = STATUS_PILL_CLASS[statusKey];
    if (add) className += " " + add;
    el.className = className.replace(/\s+/g, " ").trim();
  }

  function hydrateStatusPill(el, statusKey) {
    setText(el, STATUS_TEXT[statusKey] || STATUS_TEXT.disable);
    replacePillClass(el, statusKey);
  }

  function pickFirstDogId(sub, allDogs) {
    var subDogs = sub && Array.isArray(sub.dogs) ? sub.dogs : [];
    var candidate = subDogs.length > 0 ? subDogs[0] : null;

    if (!candidate && Array.isArray(allDogs) && allDogs.length > 0) {
      candidate = allDogs[0];
    }

    if (!candidate) return "";

    var rawId = candidate.numeric_id || candidate.id || "";
    return rawId ? String(rawId).split("/").pop() : "";
  }

  function extractNumericId(value) {
    if (!value) return "";
    return String(value).split("/").pop() || "";
  }

  function setSubscriptionIdAttrs(activeSub) {
    if (!activeSub) return;
    var rawId = activeSub.numeric_id || activeSub.id || "";
    var numericId = extractNumericId(rawId);
    if (!numericId) return;
    var freq = activeSub.frequency || "";

    var nodes = d.querySelectorAll("[data-subscription-id]");
    for (var i = 0; i < nodes.length; i++) {
      var existing = (nodes[i].getAttribute("data-subscription-id") || "").trim();
      if (!existing) {
        nodes[i].setAttribute("data-subscription-id", numericId);
      }
      var existingFreq = (nodes[i].getAttribute("data-subscription-frequency") || "").trim();
      if (!existingFreq && freq) {
        nodes[i].setAttribute("data-subscription-frequency", freq);
      }
    }
  }

  /** Fallback lookup of a dog's display name from an already-rendered .dog-card in the DOM. */
  function dogNameFromDom(rawId) {
    if (!rawId) return "";
    var raw = String(rawId);
    var numeric = raw.split("/").pop();
    var attempts = [raw, numeric];
    for (var i = 0; i < attempts.length; i++) {
      var sel = '[dog-id="' + attempts[i] + '"], [data-dog-id="' + attempts[i] + '"]';
      var el = d.querySelector(sel);
      if (el) {
        var name = el.getAttribute("dog-name") || "";
        if (name && name !== "ללא שם") return name;
      }
    }
    return "";
  }

  /** Renders the dogs list using only dogs linked to this subscription (never allDogs). */
  function renderDogsList(container, sub, allDogs) {
    if (!container) return;
    var subDogs = sub && Array.isArray(sub.dogs) ? sub.dogs : [];

    var entries = [];
    if (subDogs.length > 0) {
      entries = subDogs.map(function (dog) {
        return {
          id: dog && (dog.numeric_id || dog.id) || "",
          name: dog && dog.name ? String(dog.name) : "",
        };
      });
    } else {
      var linkedIds = collectLinkedDogIdsForSubscription(sub);
      entries = linkedIds.map(function (gid) {
        return { id: gid, name: dogNameFromDom(gid) };
      });
    }

    var html = "";
    for (var i = 0; i < entries.length; i++) {
      var name = entries[i].name;
      if (!name || name === "ללא שם") continue;
      var dogIdNum = extractNumericId(entries[i].id);
      html +=
        '<a href="#dog-' + dogIdNum + '" class="subscription-dog-item dog-name-link">' +
        '<span class="subscription-dog-item-icon" aria-hidden="true">🐾</span>' +
        '<span class="subscription-dog-item-label">' + escapeHtml(name) + "</span></a>";
    }

    if (!html) {
      container.innerHTML = '<span class="subscription-dog-item" style="opacity:0.6">אין כלבים משוייכים למנוי</span>';
      return;
    }

    container.innerHTML = html;
  }

  /* A cancelled subscription is FINAL on the server — it can never go back to active. */
  var CANCELLED_STATUSES = { disable: 1, disabled: 1, cancelled: 1, canceled: 1 };

  function toggleActionsVisibility(actionsEl, statusKey) {
    if (!actionsEl) return;

    /*
     * Nothing can be done to a cancelled subscription: the server refuses a status change
     * (cancelled is terminal), and changing the frequency or the delivery date of something
     * that will never ship again is meaningless. Offering the buttons anyway is how a
     * customer ends up pressing "activate" and being told nothing happened — so hide the
     * lot, including the two that are otherwise always on screen.
     */
    if (CANCELLED_STATUSES[statusKey]) {
      var all = actionsEl.querySelectorAll(".subscription-operation-btn");
      for (var j = 0; j < all.length; j++) all[j].setAttribute("hidden", "hidden");

      return;
    }

    var buttons = actionsEl.querySelectorAll("[data-show-when-status]");
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var showFor = (btn.getAttribute("data-show-when-status") || "").toLowerCase().trim();
      if (showFor === statusKey) {
        btn.removeAttribute("hidden");
      } else {
        btn.setAttribute("hidden", "hidden");
      }
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /** Collects every dog GID linked to a subscription, from subscription.dogs then subscription_products.dogs keys. */
  function collectLinkedDogIdsForSubscription(sub) {
    var ids = [];
    var seen = Object.create(null);

    var subDogs = sub && Array.isArray(sub.dogs) ? sub.dogs : [];
    for (var i = 0; i < subDogs.length; i++) {
      var dog = subDogs[i];
      var raw = dog && (dog.id || dog.numeric_id);
      if (!raw) continue;
      var s = String(raw);
      if (!seen[s]) { seen[s] = true; ids.push(s); }
    }

    var subProducts = sub && sub.subscription_products;
    var productDogs = subProducts && subProducts.dogs;
    if (productDogs && typeof productDogs === "object" && !Array.isArray(productDogs)) {
      for (var key in productDogs) {
        if (Object.prototype.hasOwnProperty.call(productDogs, key) && !seen[key]) {
          seen[key] = true;
          ids.push(key);
        }
      }
    }

    return ids;
  }

  /** Returns whether a dog-card element matches any linked dog GID (compares GID and numeric tail). */
  function dogElementMatchesAnyId(el, linkedIds) {
    if (!el || !linkedIds.length) return false;
    var raw = el.getAttribute("data-dog-status-id") || el.getAttribute("dog-id") || "";
    if (!raw) return false;
    var numeric = String(raw).split("/").pop();
    for (var i = 0; i < linkedIds.length; i++) {
      var candidate = linkedIds[i];
      if (candidate === raw) return true;
      if (String(candidate).split("/").pop() === numeric) return true;
    }
    return false;
  }

  function hydrateDogCardStatus(el, isLinked, subStatusKey) {
    if (!el) return;
    var className = el.className || "";
    className = className.replace(/\bstatus-(active|paused|pending|cancelled)\b/g, "").trim();
    var label;
    var addClass;
    if (isLinked && subStatusKey === "active") {
      label = STATUS_TEXT.active;
      addClass = STATUS_PILL_CLASS.active;
    } else if (subStatusKey === "paused") {
      label = STATUS_TEXT.paused;
      addClass = STATUS_PILL_CLASS.paused;
    } else if (subStatusKey === "pending") {
      label = STATUS_TEXT.pending;
      addClass = STATUS_PILL_CLASS.pending;
    } else {
      label = STATUS_TEXT.disable;
      addClass = STATUS_PILL_CLASS.disable;
    }
    if (addClass) className += " " + addClass;
    el.className = className.replace(/\s+/g, " ").trim();
    setText(el, label);
  }

  function hydrateSubscriptionPanel(activeSub, statusKey) {
    setSubscriptionIdAttrs(activeSub);

    var allDogs = (w.MillsDogDashboard && w.MillsDogDashboard.ME && w.MillsDogDashboard.ME.dogs) || [];
    forEachHydrate("sub-dogs-list", function (el) {
      renderDogsList(el, activeSub, allDogs);
    });

    var linkedDogIds = collectLinkedDogIdsForSubscription(activeSub);
    forEachHydrate("dog-card-status", function (el) {
      var matched = dogElementMatchesAnyId(el, linkedDogIds);
      hydrateDogCardStatus(el, matched, statusKey);
    });

    // Dog-card action visibility: only the buttons relevant to the dog's
    // current relationship with the subscription are shown.
    //   - In subscription → remove-from-sub + delete-dog visible, add-to-sub hidden
    //   - Not in subscription → add-to-sub visible, remove-from-sub + delete-dog hidden
    forEachHydrate("dog-action-add-to-sub", function (el) {
      var inSub = dogElementMatchesAnyId(el, linkedDogIds);
      if (inSub) el.setAttribute("hidden", "hidden");
      else el.removeAttribute("hidden");
    });
    forEachHydrate("dog-action-remove-from-sub", function (el) {
      var inSub = dogElementMatchesAnyId(el, linkedDogIds);
      if (inSub) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "hidden");
    });
    forEachHydrate("dog-action-remove-dog", function (el) {
      var inSub = dogElementMatchesAnyId(el, linkedDogIds);
      if (inSub) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "hidden");
    });

    forEachHydrate("sub-actions", function (el) {
      toggleActionsVisibility(el, statusKey);
    });

    forEachHydrate("sub-charge-cycle-wrap", function (el) {
      if (activeSub && activeSub.charge_cycle) {
        el.removeAttribute("hidden");
      } else {
        el.setAttribute("hidden", "hidden");
      }
    });

    forEachHydrate("sub-frequency-wrap", function (el) {
      if (activeSub && activeSub.frequency) {
        el.removeAttribute("hidden");
      } else {
        el.setAttribute("hidden", "hidden");
      }
    });
  }

  function hydrateMiniCartDogButton(el, sub, allDogs) {
    if (!el) return;
    var status = (sub && sub.status ? String(sub.status) : "").toLowerCase().trim();
    var dogId = pickFirstDogId(sub, allDogs);

    if (status === "active" && dogId) {
      el.setAttribute("data-dog", dogId);
      el.removeAttribute("hidden");
    } else {
      el.setAttribute("hidden", "hidden");
      el.removeAttribute("data-dog");
    }
  }

  function applyPayload(payload) {
    var data = (payload && payload.data) || null;
    if (!data) return;

    w.MillsDogDashboard = w.MillsDogDashboard || {};
    w.MillsDogDashboard.ME = data;

    var subs = Array.isArray(data.subscriptions) ? data.subscriptions : [];
    var dogs = Array.isArray(data.dogs) ? data.dogs : [];
    var activeSub = pickActiveSubscription(subs);
    var statusKey = activeSub && activeSub.status
      ? String(activeSub.status).toLowerCase().trim()
      : "disable";

    forEachHydrate("sub-count", function (el) {
      setText(el, String(subs.length));
    });

    // Counts only dogs that are "registered" — i.e. status="active" or unset
    // (treat empty/missing as active for legacy rows). Disabled dogs from the
    // Shopify fallback (Phase 2.7 added all customer dogs incl. legacy test
    // accounts) inflate the raw count, which surprises customers expecting to
    // see only their real pets.
    forEachHydrate("dogs-count", function (el) {
      var registered = dogs.filter(function (dog) {
        var status = (dog && dog.status ? String(dog.status) : "").toLowerCase().trim();
        return status === "" || status === "active";
      });
      setText(el, String(registered.length));
    });

    forEachHydrate("sub-status-text", function (el) {
      setText(el, STATUS_TEXT[statusKey] || STATUS_TEXT.disable);
    });

    forEachHydrate("sub-status-pill", function (el) {
      hydrateStatusPill(el, statusKey);
    });

    forEachHydrate("sub-frequency-text", function (el) {
      if (!activeSub) return;
      setText(el, frequencyText(activeSub.frequency));
    });

    forEachHydrate("sub-charge-cycle", function (el) {
      if (!activeSub) return;
      setText(el, formatChargeCycle(activeSub.charge_cycle));
    });

    forEachHydrate("sub-id", function (el) {
      if (!activeSub) return;
      setAttr(el, "data-subscription-id", activeSub.id || activeSub.numeric_id);
    });

    forEachHydrate("mc-dog-button", function (el) {
      hydrateMiniCartDogButton(el, activeSub, dogs);
    });

    hydrateSubscriptionPanel(activeSub, statusKey);

    // Subscription summary card is rendered by Liquid even when the customer
    // has no active subscription (the metafield-driven gate falls open for
    // a `none` status). Hide it and show a clean empty-state message instead
    // of the half-empty card with stale placeholders.
    forEachHydrate("sub-card", function (el) {
      var emptyMessageClass = "mills-no-subscriptions-message";
      var parent = el.parentNode;
      if (subs.length === 0) {
        el.setAttribute("hidden", "hidden");
        if (parent && !parent.querySelector("." + emptyMessageClass)) {
          var msg = d.createElement("div");
          msg.className = emptyMessageClass + " dashboard-empty";
          msg.textContent = "אין מנויים פעילים בחשבון";
          parent.insertBefore(msg, el);
        }
      } else {
        el.removeAttribute("hidden");
        if (parent) {
          var existing = parent.querySelector("." + emptyMessageClass);
          if (existing) existing.parentNode.removeChild(existing);
        }
      }
    });

    forEachHydrate("icount-card-required", function (el) {
      var requires = Boolean(data.flags && data.flags.any_requires_card_update);
      if (requires) {
        el.removeAttribute("hidden");
      } else {
        el.setAttribute("hidden", "hidden");
      }
    });

    forEachHydrate("customer-name", function (el) {
      // display_name is "Name · email" — show the name part only.
      var raw = data.customer && data.customer.display_name
        ? String(data.customer.display_name)
        : "";
      var name = raw.split(" · ")[0].trim();
      if (name) setText(el, name);
    });

    forEachHydrate("default-address", function (el) {
      var addrs = data.customer && Array.isArray(data.customer.addresses) ? data.customer.addresses : [];
      renderDefaultAddress(el, addrs.length > 0 ? addrs[0] : null);
    });

    forEachHydrate("dogs-grid", function (el) {
      renderDogCardsGrid(el, dogs);
    });

    // Notifies the dashboard module that subscription/dog IDs and flags are
    // populated. The products-list module runs its first render on
    // DOMContentLoaded; if `data-subscription-id` was empty at that point
    // (metafield missing) it would silently skip. Listening for this event
    // gives it a second chance once hydrate has filled the attribute.
    try {
      d.dispatchEvent(new CustomEvent("mills:storefront-me-loaded", {
        detail: {
          subscriptionId: activeSub ? (activeSub.numeric_id || activeSub.id || null) : null,
          status: statusKey,
        },
      }));
    } catch (_e) {
      /* CustomEvent unsupported — non-fatal, products render still triggers on tab switch */
    }

    hydrateOrders();

    revealPreviewGate();
  }

  function forEachHydrate(key, fn) {
    var nodes = d.querySelectorAll('[data-hydrate="' + key + '"]');
    for (var i = 0; i < nodes.length; i++) fn(nodes[i]);
  }

  /** Renders the default-address box (non-destructive — keeps existing content when no usable address). */
  function renderDefaultAddress(el, addr) {
    if (!el) return;
    var a = addr || {};
    var cityZip = [a.city, a.zip]
      .map(function (s) { return s == null ? "" : String(s).trim(); })
      .filter(Boolean)
      .join(" ");
    var lines = [a.address1, a.address2, cityZip, a.province, a.country]
      .map(function (s) { return s == null ? "" : String(s).trim(); })
      .filter(Boolean);
    if (lines.length === 0) return; // no usable address — keep existing (Liquid) content
    el.textContent = "";
    for (var i = 0; i < lines.length; i++) {
      if (i > 0) el.appendChild(d.createElement("br"));
      el.appendChild(d.createTextNode(lines[i]));
    }
  }

  var DOG_SEX_TEXT = { "0": "זכר", "1": "נקבה" };
  var DOG_ACTIVITY_TEXT = { "0": "לא פעיל", "1": "פעיל", "2": "פעיל מאד" };
  var DOG_BODY_TEXT = { "0": "רזה", "1": "כמו שצריך", "2": "מלא" };

  function escHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function dogCodedText(map, raw) {
    if (raw == null || raw === "") return "";
    var key = String(raw).trim();
    return map[key] || key;
  }

  /**
   * Builds display-only dog cards from /me when the Liquid grid rendered none
   * — the admin "Preview as customer" page (no Shopify customer context) or a
   * legacy-note customer with no dog metaobjects. Skips grids already filled
   * by Liquid so normal customers are untouched.
   */
  function renderDogCardsGrid(grid, dogs) {
    if (!grid || grid.querySelector(".dog-card")) return;
    if (!Array.isArray(dogs) || dogs.length === 0) return;

    var html = "";
    for (var i = 0; i < dogs.length; i++) {
      var dog = dogs[i] || {};
      var id = escHtml(dog.id || dog.numeric_id || "");
      var rows = [
        ["מין", dogCodedText(DOG_SEX_TEXT, dog.sex)],
        ["גיל", dog.age != null && dog.age !== "" ? escHtml(dog.age) : ""],
        ["משקל", dog.weight != null && dog.weight !== "" ? escHtml(dog.weight) + ' ק"ג' : ""],
        ["פעילות גופנית", dogCodedText(DOG_ACTIVITY_TEXT, dog.activity)],
        ["מבנה גוף", dogCodedText(DOG_BODY_TEXT, dog.body)],
        ["אלרגיות", dog.allergies ? escHtml(dog.allergies) : ""]
      ];
      var details = "";
      for (var r = 0; r < rows.length; r++) {
        if (rows[r][1]) details += "<li><strong>" + rows[r][0] + ":</strong> " + rows[r][1] + "</li>";
      }
      html +=
        '<div class="dog-card" data-dog-id="' + id + '" dog-id="' + id + '" dog-name="' + escHtml(dog.name || "") + '">' +
          '<div class="dog-info"><div class="dog-name-header"><div class="dog-name-wrapper">' +
            '<span class="dog-name dog-detail-value">' + escHtml(dog.name || "כלב") + '</span>' +
          '</div></div></div>' +
          '<ul class="dog-details">' + details + '</ul>' +
        '</div>';
    }
    grid.innerHTML = html;
  }

  // On the admin preview page the dashboard is rendered hidden; reveal it only
  // once /me has returned valid data (proving a valid preview token). On any
  // other page these elements do not exist, so this is a harmless no-op.
  function revealPreviewGate() {
    var main = d.getElementById("mills-preview-main");
    if (main) main.removeAttribute("hidden");
    var msg = d.getElementById("mills-preview-unavailable");
    if (msg && msg.parentNode) msg.parentNode.removeChild(msg);
  }

  /*
   * Order history for token-authenticated visitors.
   *
   * The Liquid table only exists for a Shopify-logged-in session; an SMS-logged-in customer
   * (and the admin preview) rendered the "no orders yet" branch forever, whatever they had
   * bought. /storefront/me/orders is the token-side source, so the same block is rebuilt
   * from it — replacing whatever Liquid produced, because the API list is the fuller one.
   */
  var ordersHydrated = false;

  var ORDER_FINANCIAL_TEXT = {
    PAID: "שולם", PENDING: "ממתין לתשלום", AUTHORIZED: "מאושר",
    REFUNDED: "זוכה", PARTIALLY_REFUNDED: "זוכה חלקית", VOIDED: "בוטל",
  };
  var ORDER_FULFILLMENT_TEXT = {
    FULFILLED: "נשלחה", UNFULFILLED: "בהכנה", PARTIALLY_FULFILLED: "נשלחה חלקית",
    SCHEDULED: "מתוזמנת", ON_HOLD: "בהמתנה",
  };

  function orderStatusText(map, raw) {
    if (!raw) return "—";
    var key = String(raw).toUpperCase();
    return map[key] || raw;
  }

  function orderDateText(iso) {
    var t = Date.parse(iso || "");
    if (isNaN(t)) return "—";
    var dt = new Date(t);
    var p = function (n) { return (n < 10 ? "0" : "") + n; };
    return p(dt.getDate()) + "." + p(dt.getMonth() + 1) + "." + dt.getFullYear();
  }

  function orderLinesHtml(items) {
    var html = '<ul class="dashboard-order-lines-list" style="list-style:none;margin:8px 0 0;padding:8px 12px;background:#f8f9fb;border-radius:8px;">';
    for (var i = 0; i < (items || []).length; i++) {
      var it = items[i] || {};
      html +=
        '<li style="display:flex;align-items:center;gap:10px;padding:6px 0;">' +
        (it.image_url ? '<img src="' + escHtml(it.image_url) + '" alt="" style="width:38px;height:38px;object-fit:cover;border-radius:6px;flex:none;">' : "") +
        '<span style="flex:1;">' + escHtml(it.title || "—") + (it.quantity > 1 ? " ×" + escHtml(it.quantity) : "") + "</span>" +
        (it.price != null ? '<span style="white-space:nowrap;">₪' + escHtml(it.price) + "</span>" : "") +
        "</li>";
    }
    return html + "</ul>";
  }

  function renderOrdersTable(wrap, orders) {
    if (!orders.length) {
      wrap.innerHTML = '<p class="dashboard-empty-message">לא ביצעת הזמנות עדיין.</p>';
      return;
    }

    var html =
      '<table class="dashboard-orders-table"><thead><tr>' +
      "<th>מספר הזמנה</th><th>תאריך הזמנה</th><th>סטטוס תשלום</th><th>סטטוס הזמנה</th><th>סה״כ</th>" +
      "</tr></thead><tbody>";

    for (var i = 0; i < orders.length; i++) {
      var o = orders[i] || {};
      html +=
        '<tr class="dashboard-order-row" data-order-toggle="' + i + '" style="cursor:pointer;">' +
        '<td><span class="dashboard-order-link">' + escHtml(o.name || "—") + "</span></td>" +
        "<td>" + orderDateText(o.created_at) + "</td>" +
        "<td>" + escHtml(orderStatusText(ORDER_FINANCIAL_TEXT, o.financial_status)) + "</td>" +
        "<td>" + escHtml(orderStatusText(ORDER_FULFILLMENT_TEXT, o.fulfillment_status)) + "</td>" +
        "<td>" + (o.total != null ? "₪" + escHtml(o.total) : "—") + "</td>" +
        "</tr>" +
        '<tr class="dashboard-order-details-row" data-order-details="' + i + '" hidden>' +
        '<td colspan="5">' + orderLinesHtml(o.line_items) + "</td>" +
        "</tr>";
    }

    wrap.innerHTML = html + "</tbody></table>";

    // Tap an order to see what was in it — SMS visitors have no Shopify order page to link to.
    wrap.addEventListener("click", function (e) {
      var row = e.target && e.target.closest ? e.target.closest("[data-order-toggle]") : null;
      if (!row) return;
      var details = wrap.querySelector('[data-order-details="' + row.getAttribute("data-order-toggle") + '"]');
      if (details) details.hidden = !details.hidden;
    });
  }

  function hydrateOrders() {
    if (ordersHydrated) return;
    var wrap = d.querySelector('[data-hydrate="orders-history"]');
    if (!wrap) return;
    ordersHydrated = true;

    var base = w.MillsAuth ? w.MillsAuth.base() : (readMeta(CONFIG.metaBase) || CONFIG.fallbackBase);
    var headers = { Accept: "application/json" };
    var token = w.MillsAuth ? w.MillsAuth.token() : (readPreviewToken() || readMeta(CONFIG.metaToken));
    if (token) headers["Authorization"] = "Bearer " + token;

    fetch(base.replace(/\/$/, "") + "/me/orders", { headers: headers })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (payload) {
        var orders = payload && payload.ok && payload.data && payload.data.orders;
        if (Array.isArray(orders)) renderOrdersTable(wrap, orders);
        // On failure the Liquid-rendered fallback (or its empty message) stays put.
      })
      .catch(function () { /* keep the Liquid fallback */ });
  }

  function run() {
    if (!shouldRun()) return;

    // Preview pages must never show cached data from a previous customer —
    // skip the sessionStorage cache entirely when a preview token is present.
    var isPreview = readPreviewToken() !== "";

    if (! isPreview) {
      var cached = readCache();
      if (cached) applyPayload(cached);
    }

    // 202 + error="sync_in_progress" means Laravel queued a ResyncCustomerJob
    // to pull this customer from Shopify into the local read model. Wait the
    // suggested interval and try again — once the job completes the next /me
    // returns the real payload. Capped at 5 attempts (~25 seconds total).
    var attempt = function (retriesLeft) {
      fetchMe()
        .then(function (payload) {
          if (payload && payload.ok && payload.data) {
            if (! isPreview) writeCache(payload);
            applyPayload(payload);
            return;
          }
          if (payload && payload.error === "sync_in_progress" && retriesLeft > 0) {
            var seconds = Number(payload.retry_after_seconds);
            if (!isFinite(seconds) || seconds < 1) seconds = 5;
            if (w.console && w.console.info) {
              w.console.info("mills.hydrate.sync_in_progress", "retry in " + seconds + "s; attempts left=" + (retriesLeft - 1));
            }
            w.setTimeout(function () { attempt(retriesLeft - 1); }, seconds * 1000);
          }
        })
        .catch(function (err) {
          if (err && err.status === 401) {
            /*
             * A token WE stored was refused: the login finally aged out. Holding onto a dead
             * token leaves the customer staring at the empty Liquid fallback with no way in
             * — drop it and send them to log in. Everyone else (no token at all, or a
             * Shopify-minted one) keeps the old behaviour: leave the fallback in place.
             */
            try {
              if (w.MillsAuth && w.localStorage.getItem(w.MillsAuth.STORAGE_KEY)) {
                w.MillsAuth.clear();
                w.location.href = "/pages/sub-login";
                return;
              }
            } catch (_) {}

            /* Not signed in or App Proxy not configured — leave Liquid fallback in place. */
            return;
          }
          if (w.console && w.console.warn) {
            w.console.warn("mills.hydrate.fetch_failed", err && err.message);
          }
        });
    };
    attempt(5);
  }

  w.MillsHydrate = {
    invalidateCache: invalidateCache,
    refresh: function () {
      invalidateCache();
      return fetchMe().then(function (payload) {
        if (payload && payload.ok && payload.data) {
          writeCache(payload);
          applyPayload(payload);
        }
        return payload;
      });
    },
    applyPayload: applyPayload,
    renderDefaultAddress: renderDefaultAddress,
  };

  if (d.readyState === "loading") {
    d.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})(window, document);
